/**
 * Poder concedente (`PC-01`, ADR-072): o que o contrato manda entregar ao regulador.
 *
 * Por concessão do recorte da pessoa: o perfil (o contrato que ele lê) e desde quando, o
 * regime de prazos em vigor ao lado — com o aviso quando ele não é o que o contrato pede —, as
 * entregas agrupadas pela forma (a concessionária envia, o regulador acessa, a concessionária
 * registra), cada uma com o prazo, a cláusula, a penalidade e **o que o cockpit já cobre**, os
 * indicadores e o autuador do contrato, e a guarda.
 *
 * **Só o administrador escolhe** o perfil, e só entre os que foram liberados para a
 * organização: o perfil cita o contrato de uma concessionária, e quem libera é a IA
 * Solutions. O servidor confere as duas coisas; aqui o seletor só não aparece para quem não
 * pode.
 *
 * **O calendário de entregas** (`PC-02`, ADR-073): o vencimento de cada entrega na janela —
 * o atrasado, o que vence e o entregue —, com o dia útil do expediente do regulador e a
 * prorrogação dita. **A supervisão registra** que a entrega saiu (o dia, o protocolo), corrige
 * registrando de novo e anula com o motivo; **o administrador informa** as datas da concessão de
 * que o calendário depende. O vencimento é do servidor; aqui, só a apresentação.
 *
 * **O envio ao regulador** (`PC-04`, ADR-074): a declaração mensal e a planilha das transações
 * saem daqui, com o pacote do fechamento do mês (`OB-06`), depois que o mês fecha. A supervisão
 * abre a prévia — o destino, o canal, o período e os números —, e só então manda; o recibo
 * registra a entrega, e a falha fica na lista dos envios, com o motivo. Mandar de novo o que já
 * tem recibo pede o motivo. Hoje o canal é um **emulador**, e a tela diz isso em toda prévia e
 * em todo recibo: nada chega ao regulador de verdade.
 *
 * **Os indicadores na gestão à vista** (`PC-08`, ADR-076): os índices do mês de cada concessão
 * — a mesma conta de Indicadores — com o acumulado dia a dia contra a meta, e a falha crítica
 * com o relógio do aviso ao regulador: quem avisou, quando, e o que está estourando, com o
 * alerta do topo da tela. Uma leitura à parte (`/tollvision/poder-concedente/indices`): se ela
 * falha, o resto da tela continua.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import type {
  AcessoDoRegulador,
  CalendarioNaTela,
  CanalNaTela,
  ConcessaoNaGestao,
  ConcessaoNoPoderConcedente,
  DataDoContrato,
  EntregaNaTela,
  EnvioNaTela,
  FalhaNaGestao,
  IndiceNaGestao,
  IndicesNaGestao,
  ItemDoCalendarioNaTela,
  NovoEnvio,
  OpcaoDePerfil,
  PerfilNaTela,
  PoderConcedenteNaTela,
  PreviaDoEnvio,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Linha,
  Painel,
} from "../componentes/base";
import { TendenciaDoIndice } from "../componentes/graficos";
import { Icone } from "../componentes/icones";
import { agoraDoInquilino, useDeslocamento } from "../relogio";
import {
  COR_DA_SITUACAO as COR_DO_INDICE,
  metaDoIndice,
  percentualDoIndice,
} from "./indices";
import {
  COR_DA_COBERTURA,
  COR_DA_SITUACAO,
  COR_DO_AVISO,
  RELEITURA_DA_GESTAO_MS,
  avisoNaHora,
  avisosDasFalhas,
  descricaoDaTendencia,
  eGestao,
  horaEmBrasilia,
  relogioDoAviso,
  textoDoAbaixo,
  textoDosAlertas,
  MENOR_MOTIVO_DO_REENVIO,
  agruparItens,
  chaveDoItem,
  contarCoberturas,
  dataDoDia,
  desdeQuando,
  diaDaSemana,
  diaEmBrasilia,
  inicioDoMesSeguinte,
  quandoDoItem,
  quandoEmBrasilia,
  shaCurto,
  tamanhoDoArquivo,
  textoDasConsultas,
  textoDasTelas,
  textoDaMeta,
  textoDaProrrogacao,
  textoDoAviso,
  textoDoCanal,
  textoDoEnvio,
  ultimoRecibo,
} from "./poderConcedente";

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

export function PoderConcedente() {
  const [tela, definirTela] = useState<PoderConcedenteNaTela | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [gestao, definirGestao] = useState<IndicesNaGestao | null>(null);
  const [erroDaGestao, definirErroDaGestao] = useState<string | null>(null);

  // Os índices são outra leitura, mais cara: se ela falha, o perfil, o calendário e o envio
  // continuam na tela. Relida de minuto em minuto, como a tela dos índices — o aviso
  // registrado em outra tela, e a suspeita nova, aparecem sozinhos —, e quando o relógio da
  // demonstração anda.
  const deslocamento = useDeslocamento();
  const ultimaLeitura = useRef(0);
  useEffect(() => {
    void deslocamento;
    // Só vale a resposta da leitura mais recente: uma lenta que chegue depois de outra mais nova
    // não volta a tela no tempo.
    const ler = () => {
      const esta = ++ultimaLeitura.current;
      return api<IndicesNaGestao>("/tollvision/poder-concedente/indices")
        .then((lida) => {
          if (esta !== ultimaLeitura.current) return;
          definirGestao(eGestao(lida) ? lida : null);
          definirErroDaGestao(null);
        })
        .catch((falha: unknown) => {
          if (esta === ultimaLeitura.current)
            definirErroDaGestao(
              mensagem(falha, "Não foi possível ler os índices do mês."),
            );
        });
    };
    void ler();
    // Com a aba escondida, não relê: ninguém olha, e para o regulador cada leitura vai à trilha.
    const id = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void ler();
    }, RELEITURA_DA_GESTAO_MS);
    return () => {
      ultimaLeitura.current++;
      window.clearInterval(id);
    };
  }, [deslocamento]);

  const carregar = useCallback(async () => {
    try {
      definirTela(
        await api<PoderConcedenteNaTela>("/tollvision/poder-concedente"),
      );
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler o poder concedente."));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Poder concedente
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            O que o contrato manda entregar ao regulador, e o que o cockpit já
            cobre
          </p>
        </div>
      </div>

      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        Cada concessão segue o perfil do contrato dela: as entregas ao
        regulador, com o prazo, a cláusula e a penalidade, e o que o cockpit já
        cobre de cada uma. O <b>regime de prazos</b> em vigor aparece ao lado —
        os dois andam separados, e a tela avisa quando o regime não é o que o
        contrato pede.
      </p>

      {gestao && <AvisosDasFalhas gestao={gestao} />}

      {/* `auto-rows-max`: sem ele o Chrome reparte a altura da tela igualmente entre os cartões
          das concessões (cada um é `min-h-0 overflow-hidden`), e cada cartão vira uma fresta
          com rolagem própria — o "Nenhum perfil…" aparecia sozinho e os índices do mês ficavam
          atrás da barra (Gisele, 03/11). Com as linhas em `max-content`, cada concessão tem a
          altura do que mostra e quem rola é esta coluna — a mesma lição do `Painel`. */}
      <div className="grid min-h-0 auto-rows-max content-start gap-3 overflow-auto">
        {erro ? (
          <Estado tipo="erro" titulo={erro} />
        ) : tela === null ? (
          <Estado tipo="carregando" titulo="Lendo o perfil das concessões…" />
        ) : tela.concessoes.length === 0 ? (
          <Estado
            tipo="vazio"
            titulo="Nenhuma concessão no seu recorte."
            detalhe="O administrador define quais concessões cada pessoa enxerga."
          />
        ) : (
          tela.concessoes.map((concessao) => (
            <ConcessaoNaTela
              key={concessao.concessao_id}
              concessao={concessao}
              oferecidos={tela.oferecidos}
              podeDefinir={tela.pode_definir}
              podeRegistrar={tela.pode_registrar}
              hoje={tela.hoje}
              canal={tela.canal ?? null}
              aoDefinir={definirTela}
              recarregar={carregar}
              gestao={
                gestao?.concessoes.find(
                  (c) => c.id === concessao.concessao_id,
                ) ?? null
              }
              periodo={gestao?.periodo ?? null}
              erroDaGestao={erroDaGestao}
            />
          ))
        )}
      </div>
    </>
  );
}

type AoMudar = {
  podeDefinir: boolean;
  podeRegistrar: boolean;
  hoje: string;
  /** Por onde a entrega sai (`PC-04`); nulo, o envio não está montado. */
  canal: CanalNaTela | null;
  /** O que o servidor devolveu depois de uma escrita da tela (a tela inteira). */
  aoDefinir: (tela: PoderConcedenteNaTela) => void;
  /** Ler de novo — depois de gravar uma data, cuja rota devolve só a configuração. */
  recarregar: () => Promise<void>;
};

function ConcessaoNaTela({
  concessao,
  oferecidos,
  gestao,
  periodo,
  erroDaGestao,
  ...mudar
}: {
  concessao: ConcessaoNoPoderConcedente;
  oferecidos: OpcaoDePerfil[];
  /** Os índices do mês desta concessão (`PC-08`). Nulo: ainda lendo, ou a leitura falhou. */
  gestao: ConcessaoNaGestao | null;
  periodo: IndicesNaGestao["periodo"] | null;
  erroDaGestao: string | null;
} & AoMudar) {
  const { podeDefinir, aoDefinir } = mudar;
  const perfil = concessao.perfil ?? null;
  return (
    <Painel
      titulo={concessao.concessao}
      acoes={
        perfil ? (
          <Badge cor="azul">{perfil.nome}</Badge>
        ) : (
          <Badge cor="cinza">sem perfil</Badge>
        )
      }
    >
      {podeDefinir && (
        <Escolha
          concessao={concessao}
          oferecidos={oferecidos}
          aoDefinir={aoDefinir}
        />
      )}
      {perfil ? (
        <Perfil concessao={concessao} perfil={perfil} {...mudar} />
      ) : podeDefinir && oferecidos.length > 0 ? null : (
        // Sem perfil, **uma linha** e os índices logo abaixo (Gisele, 03/11): o cartão grande
        // de "Nenhum perfil…" ocupava a tela e os gráficos só apareciam para quem rolasse. O
        // selo "sem perfil" já está no título, e quem pode escolher tem o seletor acima.
        <p className="text-[11.5px] text-tinta-3">
          {podeDefinir
            ? "Nenhum perfil foi liberado para esta organização. Quem libera é a IA Solutions."
            : "Quem escolhe o perfil é o administrador da concessionária."}
        </p>
      )}
      {erroDaGestao && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          {erroDaGestao}
          {gestao
            ? " Os índices abaixo são da última leitura que deu certo."
            : ""}
        </p>
      )}
      {gestao && periodo && <IndicesDoMes gestao={gestao} periodo={periodo} />}
      {concessao.regulador && (
        <AcessosDoRegulador acesso={concessao.regulador} />
      )}
      {concessao.historico.length > 0 && (
        <details className="text-[11.5px] text-tinta-3">
          <summary className="cursor-pointer">Histórico do perfil</summary>
          <ul className="mt-1 grid gap-0.5">
            {concessao.historico.map((mudanca) => (
              <li key={mudanca.desde}>
                {diaEmBrasilia(mudanca.desde)} — {mudanca.nome ?? "nenhum"}
                {mudanca.por ? `, por ${mudanca.por}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Painel>
  );
}

// --------------------------------------------------------------------------- a gestão à vista (`PC-08`)

/** O relógio da organização, de meio em meio minuto: o "faltam 25 min" anda sozinho. */
function useAgora(): Date {
  const [agora, definirAgora] = useState(() => agoraDoInquilino());
  useEffect(() => {
    const id = window.setInterval(
      () => definirAgora(agoraDoInquilino()),
      30_000,
    );
    return () => window.clearInterval(id);
  }, []);
  return agora;
}

/**
 * O topo da tela: a falha crítica sem aviso — o prazo correndo ou vencido — e a suspeita que
 * espera a supervisão. É o que o diretor precisa ver antes de tudo.
 */
function AvisosDasFalhas({ gestao }: { gestao: IndicesNaGestao }) {
  const agora = useAgora();
  const { criticos, atencao } = avisosDasFalhas(gestao.concessoes, agora);
  return (
    <>
      {criticos.length > 0 && (
        <ListaDeAvisos
          rotulo="Falha crítica sem aviso ao regulador"
          avisos={criticos}
          cor="vermelho"
        />
      )}
      {atencao.length > 0 && (
        <ListaDeAvisos
          rotulo="Pórticos parados à espera da supervisão"
          avisos={atencao}
          cor="amarelo"
        />
      )}
    </>
  );
}

function ListaDeAvisos({
  rotulo,
  avisos,
  cor,
}: {
  rotulo: string;
  avisos: string[];
  cor: "amarelo" | "vermelho";
}) {
  return (
    <ul
      aria-label={rotulo}
      className={`grid gap-1 rounded-cartao border px-3 py-2 text-[11.5px] text-tinta ${
        cor === "vermelho"
          ? "border-vermelho-borda bg-vermelho-fundo"
          : "border-amarelo-borda bg-amarelo-fundo"
      }`}
    >
      {avisos.map((a) => (
        <li key={a} className="flex items-start gap-1.5">
          <span
            className={cor === "vermelho" ? "text-vermelho" : "text-amarelo"}
          >
            <Icone.Alerta />
          </span>
          {a}
        </li>
      ))}
    </ul>
  );
}

/**
 * Os índices do mês da concessão, contra a meta, com a tendência — e a falha crítica com o
 * relógio do aviso. Os números são os de Indicadores; quem decide a inoperância e registra o
 * aviso continua sendo a tela dos índices.
 */
function IndicesDoMes({
  gestao,
  periodo,
}: {
  gestao: ConcessaoNaGestao;
  periodo: IndicesNaGestao["periodo"];
}) {
  return (
    <section aria-label="Os índices do mês" className="grid gap-2">
      <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        Os índices do mês · {periodo.rotulo}
      </h3>
      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        {periodo.fechado
          ? "O mês fechou: estes são os números dele."
          : "O mês está em curso: o acumulado anda até o fim dele."}{" "}
        A linha é o mês acumulado do dia 1 até cada dia, contra a meta tracejada
        — a mesma conta que o regulador fecha no fim do mês. A meta é a do
        regime da concessão; sem ela, a da resolução federal, como referência.{" "}
        <Link to="/indicadores/indices" className="text-azul hover:underline">
          Abrir os índices e os pórticos
        </Link>
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {gestao.indices.map((indice) => (
          <CartaoDaTendencia key={indice.indice} indice={indice} />
        ))}
      </div>
      <FalhasCriticas gestao={gestao} />
    </section>
  );
}

function CartaoDaTendencia({ indice: i }: { indice: IndiceNaGestao }) {
  const cor = COR_DO_INDICE[i.situacao];
  const numero =
    cor === "vermelho"
      ? "text-vermelho"
      : cor === "amarelo"
        ? "text-amarelo"
        : cor === "verde"
          ? "text-verde"
          : "text-tinta";
  const abaixo = textoDoAbaixo(i);
  return (
    <section
      aria-label={i.rotulo}
      title={i.definicao}
      className="grid content-start gap-1 rounded-cartao border border-linha bg-painel-2 p-2.5"
    >
      <span className="flex items-start justify-between gap-2">
        <span className="text-[11.5px] leading-tight text-tinta-3">
          {i.rotulo}
        </span>
        <Badge cor={cor}>{i.situacao_rotulo}</Badge>
      </span>
      <b className={`mono text-[22px] leading-none font-extrabold ${numero}`}>
        {i.situacao === "SEM_FONTE" ? "—" : percentualDoIndice(i.percentual)}
      </b>
      <span className="text-[10.5px] text-tinta-3">{metaDoIndice(i)}</span>
      {i.situacao === "SEM_FONTE" ? (
        i.nota && (
          <span className="text-[10.5px] leading-snug text-tinta-3">
            {i.nota}
          </span>
        )
      ) : (
        <TendenciaDoIndice
          pontos={i.tendencia}
          minimo={i.meta?.minimo == null ? null : Number(i.meta.minimo)}
          descricao={descricaoDaTendencia(i)}
        />
      )}
      {abaixo && (
        <span className="text-[10.5px] font-semibold text-vermelho">
          {abaixo}
        </span>
      )}
    </section>
  );
}

const ROTULO_DA_FALHA: Record<FalhaNaGestao["situacao"], string> = {
  SUSPEITA: "suspeita",
  CONFIRMADA: "crítica",
  DESCARTADA: "descartada",
};

/**
 * A falha crítica do mês e a suspeita em aberto — a suspeita pode ser crítica, e o prazo do
 * aviso conta do início dela. Quem avisou, quando, o protocolo, e o que o cockpit alertou.
 */
function FalhasCriticas({ gestao }: { gestao: ConcessaoNaGestao }) {
  const agora = useAgora();
  return (
    <section
      aria-label="A falha crítica e o aviso ao regulador"
      className="grid gap-1.5"
    >
      <h4 className="text-[11.5px] font-bold text-tinta-2">
        A falha crítica e o aviso ao regulador
      </h4>
      {gestao.aviso_de_falha && (
        <p className="text-[11px] text-tinta-3">{gestao.aviso_de_falha}</p>
      )}
      {gestao.falhas.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">
          Nenhuma falha crítica no mês, e nenhum pórtico parado à espera da
          supervisão.
        </p>
      ) : (
        <ul aria-label="Falhas do mês" className="grid gap-1.5">
          {gestao.falhas.map((f) => {
            const alertas = textoDosAlertas(f.alertas, agora);
            const aviso = avisoNaHora(f, agora);
            return (
              <li
                key={f.id}
                className="grid gap-0.5 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-[11.5px] text-tinta-2"
              >
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <b className="text-tinta">{f.praca}</b>
                  <span className="mono text-tinta-3">
                    desde {horaEmBrasilia(f.inicio, agora)}
                    {f.fim
                      ? ` até ${horaEmBrasilia(f.fim, agora)}`
                      : ", em aberto"}
                  </span>
                  <Badge cor={f.critica ? "vermelho" : "amarelo"}>
                    {ROTULO_DA_FALHA[f.situacao]}
                  </Badge>
                  <Badge cor={COR_DO_AVISO[aviso.aviso]}>{aviso.rotulo}</Badge>
                </span>
                <span
                  className={
                    aviso.aviso === "VENCIDO" || aviso.aviso === "FORA_DO_PRAZO"
                      ? "text-vermelho"
                      : "text-tinta-2"
                  }
                >
                  {relogioDoAviso(f, agora)}
                  {f.protocolo_do_aviso
                    ? ` · protocolo ${f.protocolo_do_aviso}`
                    : ""}
                  {f.avisado_por ? ` · por ${f.avisado_por}` : ""}.
                </span>
                {f.causa_rotulo && (
                  <span className="text-tinta-3">Causa: {f.causa_rotulo}.</span>
                )}
                {alertas && <span className="text-tinta-3">{alertas}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Quem do poder concedente lê o cockpit nesta concessão, e o que leu (`PC-05`, ADR-075): a
 * trilha das leituras do perfil Regulador, dia a dia. Só a concessionária vê isto — o
 * servidor não o manda ao próprio regulador.
 */
function AcessosDoRegulador({ acesso }: { acesso: AcessoDoRegulador }) {
  return (
    <section aria-label="Acessos do regulador" className="grid gap-2">
      <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        Acessos do regulador
      </h3>
      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        Quem do poder concedente lê o cockpit nesta concessão, e o que leu nos
        últimos {acesso.janela_dias} dias. O acesso é só de leitura, sem a foto
        de pista; quem cria e revoga é o administrador, em Administração.
      </p>
      <ul aria-label="Reguladores com acesso" className="grid gap-1">
        {acesso.reguladores.map((r) => (
          <li
            key={r.email}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-tinta-2"
          >
            <b className="text-tinta">{r.nome}</b>
            <span className="text-tinta-3">{r.email}</span>
            <Badge cor={r.ativo ? "verde" : "cinza"}>
              {r.ativo ? "ativo" : "revogado"}
            </Badge>
            <Badge cor={r.segundo_fator ? "verde" : "amarelo"}>
              {r.segundo_fator ? "com segundo fator" : "sem segundo fator"}
            </Badge>
            <span className="text-tinta-3">
              {r.ultima_leitura
                ? `última leitura em ${quandoEmBrasilia(r.ultima_leitura)}`
                : "nunca leu"}
            </span>
          </li>
        ))}
      </ul>
      {acesso.dias.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">
          Nenhuma consulta nos últimos {acesso.janela_dias} dias.
        </p>
      ) : (
        <ul aria-label="O que o regulador viu" className="grid gap-1.5">
          {acesso.dias.map((dia) => (
            <li
              key={`${dia.dia}-${dia.regulador}`}
              className="grid gap-0.5 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-[11.5px] text-tinta-2"
            >
              <span className="flex flex-wrap items-center gap-x-2">
                <b className="mono text-tinta">{dataDoDia(dia.dia)}</b>
                <span>{dia.regulador}</span>
                <span
                  className={dia.recusadas ? "text-amarelo" : "text-tinta-3"}
                >
                  {textoDasConsultas(dia)}
                </span>
              </span>
              {dia.telas.length > 0 && (
                <span>Viu {textoDasTelas(dia.telas)}.</span>
              )}
              {dia.transitos.length > 0 && (
                <span className="flex flex-wrap items-center gap-1">
                  Abriu
                  {dia.transitos.map((t) => (
                    <Link
                      key={t.id}
                      to={`/senatran?busca=${encodeURIComponent(t.placa ?? t.id)}`}
                      className="mono rounded-[3px] border border-linha bg-painel px-1 text-tinta hover:bg-realce"
                    >
                      {t.placa ?? "sem placa"}
                    </Link>
                  ))}
                  {dia.mais_transitos > 0 && (
                    <span className="text-tinta-3">
                      e mais {dia.mais_transitos}
                    </span>
                  )}
                </span>
              )}
              {dia.recusas.length > 0 && (
                <span className="text-tinta-3">
                  O cockpit recusou: {textoDasTelas(dia.recusas)}.
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Escolha({
  concessao,
  oferecidos,
  aoDefinir,
}: {
  concessao: ConcessaoNoPoderConcedente;
  oferecidos: OpcaoDePerfil[];
  aoDefinir: (tela: PoderConcedenteNaTela) => void;
}) {
  const atual = concessao.perfil?.codigo ?? "";
  const [escolhido, definirEscolhido] = useState(atual);
  const [gravando, definirGravando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => definirEscolhido(atual), [atual]);

  if (oferecidos.length === 0 && !atual) return null;

  async function definir() {
    definirGravando(true);
    try {
      aoDefinir(
        await api<PoderConcedenteNaTela>(
          `/tollvision/poder-concedente/${concessao.concessao_id}`,
          { method: "POST", corpo: { perfil: escolhido || null } },
        ),
      );
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível definir o perfil."));
    } finally {
      definirGravando(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5 text-[11.5px] text-tinta-3">
      <label className="inline-flex flex-wrap items-center gap-1.5">
        Perfil desta concessão
        <select
          value={escolhido}
          aria-label={`Perfil do poder concedente de ${concessao.concessao}`}
          onChange={(e) => definirEscolhido(e.target.value)}
          className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
        >
          <option value="">Nenhum</option>
          {/* O perfil em vigor que deixou de ser liberado continua na lista: sem ele, o
              seletor mostraria "Nenhum" e o administrador não conseguiria tirá-lo. */}
          {concessao.perfil && !oferecidos.some((o) => o.codigo === atual) && (
            <option value={atual}>{concessao.perfil.nome}</option>
          )}
          {oferecidos.map((opcao) => (
            <option key={opcao.codigo} value={opcao.codigo}>
              {opcao.nome}
            </option>
          ))}
        </select>
      </label>
      <Botao
        papel="primaria"
        disabled={gravando || escolhido === atual}
        onClick={() => void definir()}
      >
        Definir
      </Botao>
      <span>
        Vale a partir de agora, fica no histórico e na trilha administrativa.
      </span>
      {erro && (
        <p role="alert" className="w-full text-vermelho">
          {erro}
        </p>
      )}
    </div>
  );
}

function Perfil({
  concessao,
  perfil,
  ...mudar
}: {
  concessao: ConcessaoNoPoderConcedente;
  perfil: PerfilNaTela;
} & AoMudar) {
  const contagem = contarCoberturas(perfil);
  const aviso = textoDoAviso(perfil.aviso_de_falha_horas);
  return (
    <>
      <p className="text-[11.5px] text-tinta-3">
        <b className="text-tinta-2">{perfil.poder_concedente}</b> ·{" "}
        {perfil.contrato} · {desdeQuando(concessao)}
      </p>

      {concessao.aviso_do_regime && (
        <p
          role="note"
          className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo p-2.5 text-[11.5px] text-amarelo"
        >
          {concessao.aviso_do_regime}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-tinta-3">
        <span>No cockpit:</span>
        {(["PRONTA", "PARCIAL", "AINDA_NAO", "FORA_DO_COCKPIT"] as const)
          .filter((c) => contagem[c] > 0)
          .map((c) => (
            <Badge key={c} cor={COR_DA_COBERTURA[c]}>
              {contagem[c]} {ROTULO_DA_CONTAGEM[c]}
            </Badge>
          ))}
        <span>
          {textoDoCanal(mudar.canal, concessao.calendario?.itens ?? [])}
        </span>
      </div>

      {concessao.calendario && (
        <CalendarioDeEntregas
          concessao={concessao}
          calendario={concessao.calendario}
          {...mudar}
        />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Cartao titulo="O contrato">
          <Linha rotulo="Regime do contrato">
            {perfil.regime_do_contrato.nome}
          </Linha>
          <Linha rotulo="Regime em vigor">
            {concessao.regime_em_vigor.nome}
          </Linha>
          <Linha rotulo="Autuador">{perfil.autuador}</Linha>
          {aviso && (
            <Linha rotulo="Aviso de falha crítica">em até {aviso}</Linha>
          )}
          {perfil.metas.map((meta) => (
            <Linha key={meta.indice} rotulo={meta.indice}>
              {textoDaMeta(meta.minimo)}
            </Linha>
          ))}
        </Cartao>
        <Cartao titulo="Guarda">
          {perfil.guardas.map((guarda) => (
            <div key={guarda.o_que} className="grid gap-0.5 text-[11.5px]">
              <span className="text-tinta-2">
                {guarda.o_que}: <b>{guarda.prazo}</b>
              </span>
              <span className="text-tinta-3">
                {guarda.clausula} ·{" "}
                <Badge cor={COR_DA_COBERTURA[guarda.cobertura]}>
                  {guarda.cobertura_rotulo}
                </Badge>{" "}
                {guarda.no_cockpit}
              </span>
            </div>
          ))}
        </Cartao>
      </div>

      {perfil.grupos.map((grupo) => (
        <section key={grupo.forma} className="grid gap-2">
          <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
            {grupo.rotulo}
          </h3>
          <ul className="grid gap-2 lg:grid-cols-2">
            {grupo.entregas.map((entrega) => (
              <EntregaDoPerfil key={entrega.codigo} entrega={entrega} />
            ))}
          </ul>
        </section>
      ))}

      {perfil.pendente && (
        <p className="text-[11.5px] text-tinta-3">
          <b>Pendente:</b> {perfil.pendente}
        </p>
      )}
      <p className="text-[11px] text-tinta-3">Fonte: {perfil.fonte}.</p>
    </>
  );
}

const ROTULO_DA_CONTAGEM: Record<EntregaNaTela["cobertura"], string> = {
  PRONTA: "prontas",
  PARCIAL: "parciais",
  AINDA_NAO: "ainda não",
  FORA_DO_COCKPIT: "fora do cockpit",
};

function EntregaDoPerfil({ entrega }: { entrega: EntregaNaTela }) {
  return (
    <li
      aria-label={entrega.nome}
      className="grid content-start gap-1.5 rounded-cartao border border-linha bg-painel-2 p-2.5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-tinta">{entrega.nome}</b>
        <span className="ml-auto">
          <Badge cor={COR_DA_COBERTURA[entrega.cobertura]}>
            {entrega.cobertura_rotulo}
          </Badge>
        </span>
      </div>
      <p className="text-[11.5px] text-tinta-3">{entrega.descricao}</p>
      <Linha rotulo="Para">{entrega.destinatarios.join(" e ")}</Linha>
      <Linha rotulo="Quando">{entrega.periodicidade}</Linha>
      <Linha rotulo="Prazo">{entrega.prazo}</Linha>
      <Linha rotulo="Cláusula">{entrega.clausula}</Linha>
      {entrega.penalidade && (
        <Linha rotulo="Se falhar">{entrega.penalidade}</Linha>
      )}
      <p className="text-[11.5px] text-tinta-2">
        <b>No cockpit:</b> {entrega.no_cockpit}
      </p>
    </li>
  );
}

// --------------------------------------------------------------------------- o calendário (`PC-02`)

function CalendarioDeEntregas({
  concessao,
  calendario,
  ...mudar
}: {
  concessao: ConcessaoNoPoderConcedente;
  calendario: CalendarioNaTela;
} & AoMudar) {
  const { atrasadas, aVencer, entregues } = agruparItens(calendario.itens);
  return (
    <section aria-label="Calendário de entregas" className="grid gap-2">
      <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        Calendário de entregas
      </h3>
      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        De {dataDoDia(calendario.de)} a {dataDoDia(calendario.ate)}. O dia útil
        é o do expediente do regulador ({calendario.expediente}): sem sábado,
        domingo e feriado de lei. O vencimento que cai sem expediente vai para o
        dia útil seguinte; o ponto facultativo só avisa.
        {mudar.podeRegistrar
          ? " Registre cada entrega quando ela sair: o registro é o que tira a entrega do atraso. A que sai pelo cockpit se registra sozinha, com o recibo."
          : " Quem registra e envia as entregas é a supervisão."}
      </p>
      {calendario.aviso_dos_pontos && (
        <p
          role="note"
          className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo p-2.5 text-[11.5px] text-amarelo"
        >
          {calendario.aviso_dos_pontos}
        </p>
      )}

      <ListaDoCalendario
        titulo="Atrasadas"
        vazio="Nenhuma entrega atrasada na janela."
        itens={atrasadas}
        concessao={concessao}
        {...mudar}
      />
      <ListaDoCalendario
        titulo="A vencer"
        vazio="Nada vence na janela."
        itens={aVencer}
        concessao={concessao}
        {...mudar}
      />
      {entregues.length > 0 && (
        <details className="grid gap-2">
          <summary className="cursor-pointer text-[11.5px] text-tinta-3">
            Entregues na janela ({entregues.length})
          </summary>
          <ListaDoCalendario
            titulo="Entregues"
            vazio=""
            itens={entregues}
            concessao={concessao}
            {...mudar}
          />
        </details>
      )}

      {calendario.sem_data.length > 0 && (
        <div
          role="note"
          className="rounded-cartao border border-linha bg-painel-2 p-2.5 text-[11.5px] text-tinta-3"
        >
          <b className="text-tinta-2">Sem data no calendário:</b>
          <ul className="mt-1 grid gap-0.5">
            {calendario.sem_data.map((s) => (
              <li key={s.entrega}>
                {s.nome} — falta {s.falta}.
              </li>
            ))}
          </ul>
          <span>
            {mudar.podeDefinir
              ? "Informe abaixo, em Datas do contrato."
              : "Quem informa é o administrador da concessionária."}
          </span>
        </div>
      )}

      <DatasDoContrato
        concessao={concessao}
        datas={calendario.datas}
        {...mudar}
      />
    </section>
  );
}

function ListaDoCalendario({
  titulo,
  vazio,
  itens,
  concessao,
  ...mudar
}: {
  titulo: string;
  vazio: string;
  itens: ItemDoCalendarioNaTela[];
  concessao: ConcessaoNoPoderConcedente;
} & AoMudar) {
  return (
    <div className="grid gap-1.5">
      <h4 className="text-[11px] font-semibold text-tinta-2">
        {titulo} ({itens.length})
      </h4>
      {itens.length === 0 ? (
        <p className="text-[11.5px] text-tinta-3">{vazio}</p>
      ) : (
        <ul aria-label={titulo} className="grid gap-1.5 lg:grid-cols-2">
          {itens.map((item) => (
            <ItemDoCalendario
              key={chaveDoItem(item)}
              item={item}
              concessao={concessao}
              {...mudar}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

type Aberto = "registrar" | "anular" | "enviar" | null;

function ItemDoCalendario({
  item,
  concessao,
  podeRegistrar,
  hoje,
  canal,
  aoDefinir,
  recarregar,
}: {
  item: ItemDoCalendarioNaTela;
  concessao: ConcessaoNoPoderConcedente;
} & AoMudar) {
  const [aberto, definirAberto] = useState<Aberto>(null);
  const [entregueEm, definirEntregueEm] = useState(hoje);
  const [protocolo, definirProtocolo] = useState("");
  const [observacao, definirObservacao] = useState("");
  const [motivo, definirMotivo] = useState("");
  const [gravando, definirGravando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const registro = item.registro ?? null;
  const recibo = ultimoRecibo(item);
  // O nome e a competência do item descrevem cada botão dele: com vários "Registrar entrega"
  // na tela, o leitor de tela diz de qual.
  const titulo = useId();
  const prorrogacao = textoDaProrrogacao(item);
  const base = `/tollvision/poder-concedente/${concessao.concessao_id}/entregas`;

  function abrir(qual: Aberto) {
    definirErro(null);
    definirEntregueEm(hoje);
    definirProtocolo("");
    definirObservacao("");
    definirMotivo("");
    definirAberto(qual);
  }

  async function enviar(url: string, corpo: unknown, padrao: string) {
    definirGravando(true);
    try {
      aoDefinir(
        await api<PoderConcedenteNaTela>(url, { method: "POST", corpo }),
      );
      definirAberto(null);
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, padrao));
    } finally {
      definirGravando(false);
    }
  }

  const registrar = () =>
    enviar(
      base,
      {
        entrega: item.entrega,
        competencia: item.competencia ?? null,
        entregue_em: entregueEm,
        protocolo: protocolo.trim() || null,
        observacao: observacao.trim() || null,
      },
      "Não foi possível registrar a entrega.",
    );
  const anular = () =>
    registro
      ? enviar(
          `${base}/${registro.id}/anular`,
          { motivo: motivo.trim() },
          "Não foi possível anular o registro.",
        )
      : Promise.resolve();

  return (
    <li
      aria-label={`${item.nome}, ${item.rotulo}`}
      className="grid content-start gap-1 rounded-cartao border border-linha bg-painel-2 p-2.5 text-[11.5px]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span id={titulo} className="contents">
          <b className="text-tinta">{item.nome}</b>
          <span className="text-tinta-3">{item.rotulo}</span>
        </span>
        <span className="ml-auto">
          <Badge cor={COR_DA_SITUACAO[item.situacao]}>
            {item.situacao_rotulo}
          </Badge>
        </span>
      </div>
      <span className="text-tinta-2">
        Vence em <b>{dataDoDia(item.vence_em)}</b> ({diaDaSemana(item.vence_em)}
        ) — {quandoDoItem(item)} · para {item.destinatarios.join(" e ")}
      </span>
      {prorrogacao && <span className="text-tinta-3">{prorrogacao}</span>}
      {item.ponto_facultativo && (
        <span className="text-amarelo">
          {item.ponto_facultativo}: o regulador pode estar sem expediente; o
          vencimento não muda.
        </span>
      )}
      {registro && (
        <span className="text-tinta-3">
          Registrada{registro.por ? ` por ${registro.por}` : ""} em{" "}
          {diaEmBrasilia(registro.em)}
          {registro.protocolo ? ` · protocolo ${registro.protocolo}` : ""}
          {registro.observacao ? ` · ${registro.observacao}` : ""}
          {registro.anteriores > 0
            ? ` · corrige ${registro.anteriores} ${registro.anteriores === 1 ? "registro anterior" : "registros anteriores"}`
            : ""}
        </span>
      )}

      {item.envios.length > 0 && <EnviosDoItem envios={item.envios} />}

      {podeRegistrar && aberto === null && !item.registravel && (
        // A ação que o estado não permite não aparece muda (§4, regra 4): diz por quê.
        <span className="text-tinta-3">
          Registra-se quando a competência começar.
        </span>
      )}
      {podeRegistrar &&
        aberto === null &&
        item.registravel &&
        item.sai_pelo_cockpit &&
        !item.enviavel &&
        item.competencia && (
          <span className="text-tinta-3">
            Sai pelo cockpit quando o mês fechar, a partir de{" "}
            {dataDoDia(inicioDoMesSeguinte(item.competencia))}.
          </span>
        )}
      {podeRegistrar && aberto === null && item.registravel && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {item.enviavel && canal && (
            // Mandar pela primeira vez é a ação da entrega; mandar de novo, não.
            <Botao
              papel={recibo ? "secundaria" : "primaria"}
              aria-describedby={titulo}
              onClick={() => abrir("enviar")}
            >
              {recibo ? "Enviar de novo" : "Enviar ao regulador"}
            </Botao>
          )}
          <Botao
            papel={registro || item.enviavel ? "secundaria" : "primaria"}
            aria-describedby={titulo}
            onClick={() => abrir("registrar")}
          >
            {registro ? "Corrigir" : "Registrar entrega"}
          </Botao>
          {registro && (
            // Ação destrutiva longe da frequente (§4, regra 5).
            <span className="ml-auto">
              <Botao aria-describedby={titulo} onClick={() => abrir("anular")}>
                Anular registro
              </Botao>
            </span>
          )}
        </div>
      )}

      {aberto === "registrar" && (
        <div className="grid gap-1.5 rounded-cartao border border-linha bg-painel p-2">
          <label className="grid gap-0.5 text-tinta-3">
            Entregue em
            <input
              type="date"
              value={entregueEm}
              max={hoje}
              onChange={(e) => definirEntregueEm(e.target.value)}
              className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
            />
          </label>
          <label className="grid gap-0.5 text-tinta-3">
            Protocolo (opcional)
            <input
              value={protocolo}
              maxLength={200}
              onChange={(e) => definirProtocolo(e.target.value)}
              className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
            />
          </label>
          <label className="grid gap-0.5 text-tinta-3">
            Observação (opcional)
            <input
              value={observacao}
              maxLength={1000}
              onChange={(e) => definirObservacao(e.target.value)}
              className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Botao
              papel="aprovar"
              disabled={gravando || !entregueEm}
              onClick={() => void registrar()}
            >
              Gravar a entrega
            </Botao>
            <Botao onClick={() => definirAberto(null)}>Cancelar</Botao>
            <span className="text-tinta-3">
              {registro
                ? "Corrigir é registrar de novo: vale o último, e o anterior fica na trilha."
                : "Fica na trilha administrativa, com o seu nome."}
            </span>
          </div>
        </div>
      )}

      {aberto === "enviar" && item.competencia && (
        <EnvioAoRegulador
          concessao={concessao}
          item={item}
          competencia={item.competencia}
          recibo={recibo}
          titulo={titulo}
          aoDefinir={(nova) => {
            aoDefinir(nova);
            definirAberto(null);
          }}
          aoFalhar={recarregar}
          aoCancelar={() => definirAberto(null)}
        />
      )}

      {aberto === "anular" && registro && (
        <div className="grid gap-1.5 rounded-cartao border border-vermelho-borda bg-vermelho-fundo p-2">
          <label className="grid gap-0.5 text-tinta-2">
            Motivo da anulação
            <input
              value={motivo}
              maxLength={1000}
              onChange={(e) => definirMotivo(e.target.value)}
              className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Botao
              papel="perigo"
              disabled={gravando || motivo.trim().length < MENOR_MOTIVO}
              onClick={() => void anular()}
            >
              Anular o registro
            </Botao>
            <Botao onClick={() => definirAberto(null)}>Cancelar</Botao>
            <span className="text-tinta-2">
              Nada se apaga: a anulação fica na trilha, com o motivo (pelo menos{" "}
              {MENOR_MOTIVO} caracteres).
            </span>
          </div>
        </div>
      )}

      {erro && (
        <p role="alert" className="text-vermelho">
          {erro}
        </p>
      )}
    </li>
  );
}

function EnviosDoItem({ envios }: { envios: EnvioNaTela[] }) {
  return (
    <ul aria-label="Envios ao regulador" className="grid gap-0.5 text-tinta-3">
      {envios.map((envio) => (
        // O badge corre com a frase: numa linha flexível, a frase longa o deixava sozinho.
        <li key={envio.id} className="leading-relaxed">
          <Badge cor={envio.situacao === "RECEBIDO" ? "verde" : "vermelho"}>
            {envio.situacao === "RECEBIDO" ? "recebido" : "sem recibo"}
          </Badge>{" "}
          <span>
            {textoDoEnvio(envio)}
            {envio.situacao === "RECEBIDO" && (
              <>
                {" "}
                · {envio.arquivo}, {tamanhoDoArquivo(envio.tamanho)} · SHA-256{" "}
                <code title={envio.sha256} className="text-tinta-2">
                  {shaCurto(envio.sha256)}
                </code>
              </>
            )}
            {envio.motivo_do_reenvio
              ? ` · reenvio: ${envio.motivo_do_reenvio}`
              : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A prévia do envio e o envio (`PC-04`): o que vai, para quem, por onde, e o que impede —
 * antes de mandar. O reenvio pede o motivo antes do botão.
 */
function EnvioAoRegulador({
  concessao,
  item,
  competencia,
  recibo,
  aoDefinir,
  aoFalhar,
  aoCancelar,
  titulo,
}: {
  concessao: ConcessaoNoPoderConcedente;
  item: ItemDoCalendarioNaTela;
  /** O id do nome e da competência do item: a descrição de cada botão do quadro. */
  titulo: string;
  competencia: string;
  recibo: EnvioNaTela | null;
  aoDefinir: (tela: PoderConcedenteNaTela) => void;
  /** Ler a tela de novo: a tentativa que falhou fica gravada, e a lista dos envios a mostra. */
  aoFalhar: () => Promise<void>;
  aoCancelar: () => void;
}) {
  const [previa, definirPrevia] = useState<PreviaDoEnvio | null>(null);
  const [erroDaPrevia, definirErroDaPrevia] = useState<string | null>(null);
  const [motivo, definirMotivo] = useState("");
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const base = `/tollvision/poder-concedente/${concessao.concessao_id}/envios`;

  useEffect(() => {
    let vale = true;
    const busca = new URLSearchParams({ entrega: item.entrega, competencia });
    api<PreviaDoEnvio>(`${base}/previa?${busca.toString()}`)
      .then((lida) => vale && definirPrevia(lida))
      .catch(
        (falha: unknown) =>
          vale &&
          definirErroDaPrevia(
            mensagem(falha, "Não foi possível ler a prévia."),
          ),
      );
    return () => {
      vale = false;
    };
  }, [base, item.entrega, competencia]);

  // O recibo que chegou depois de a prévia ser lida (outra pessoa mandou no meio) também
  // vale: sem isto, o quadro pedia "Enviar" sem o campo do motivo, e o servidor recusava.
  const reenvio = recibo !== null || previa?.reenvio === true;
  const faltaMotivo = reenvio && motivo.trim().length < MENOR_MOTIVO_DO_REENVIO;
  const impedido = previa === null || previa.problemas.length > 0;

  async function enviar() {
    definirEnviando(true);
    const corpo: NovoEnvio = {
      entrega: item.entrega,
      competencia,
      motivo_do_reenvio: reenvio ? motivo.trim() : null,
    };
    try {
      aoDefinir(
        await api<PoderConcedenteNaTela>(base, { method: "POST", corpo }),
      );
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível enviar."));
      await aoFalhar();
    } finally {
      definirEnviando(false);
    }
  }

  return (
    <section
      aria-label="Prévia do envio ao regulador"
      className="grid gap-1.5 rounded-cartao border border-linha bg-painel p-2"
    >
      {erroDaPrevia ? (
        <p role="alert" className="text-vermelho">
          {erroDaPrevia}
        </p>
      ) : previa === null ? (
        <p className="text-tinta-3">Montando a prévia do pacote…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <b className="text-tinta">Enviar a {previa.destinatario}</b>
            {previa.simulado && (
              <Badge cor="amarelo">
                emulador — nada sai para a {previa.destinatario}
              </Badge>
            )}
          </div>
          <Linha rotulo="Pelo">{previa.canal}</Linha>
          <Linha rotulo="Período">{previa.periodo}</Linha>
          <p className="text-tinta-2">
            <b>O que vai:</b> {previa.conteudo}
          </p>
          <Linha rotulo="Arquivo">{previa.arquivo}</Linha>
          <Linha rotulo="No pacote">
            {previa.evasoes.toLocaleString("pt-BR")}{" "}
            {previa.evasoes === 1 ? "evasão" : "evasões"},{" "}
            {previa.fraudes.toLocaleString("pt-BR")}{" "}
            {previa.fraudes === 1 ? "fraude" : "fraudes"} e{" "}
            {previa.fotos.toLocaleString("pt-BR")}{" "}
            {previa.fotos === 1 ? "foto anonimizada" : "fotos anonimizadas"}
          </Linha>
          {previa.linhas.length > 0 && (
            <details className="text-tinta-3">
              <summary className="cursor-pointer">O resumo do mês</summary>
              <ul className="mt-1 grid gap-0.5">
                {previa.linhas.map((linha) => (
                  <li key={linha.rotulo}>
                    {linha.rotulo}: {linha.quantidade.toLocaleString("pt-BR")}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {previa.problemas.length > 0 && (
            <p
              role="note"
              className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo p-2 text-amarelo"
            >
              {previa.problemas.join(" ")}
            </p>
          )}
          {reenvio && (
            <label className="grid gap-0.5 text-tinta-2">
              {previa.ultimo_protocolo
                ? `Esta competência já tem recibo (protocolo ${previa.ultimo_protocolo}). Motivo do reenvio`
                : "Esta competência já tem recibo. Motivo do reenvio"}
              <input
                value={motivo}
                maxLength={1000}
                onChange={(e) => definirMotivo(e.target.value)}
                className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]"
              />
            </label>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Botao
          papel="aprovar"
          aria-describedby={titulo}
          disabled={enviando || impedido || faltaMotivo}
          onClick={() => void enviar()}
        >
          {enviando ? "Enviando…" : reenvio ? "Enviar de novo" : "Enviar"}
        </Botao>
        <Botao
          aria-describedby={titulo}
          disabled={enviando}
          onClick={aoCancelar}
        >
          Cancelar
        </Botao>
        <span className="text-tinta-3">
          {reenvio
            ? `Vale o último recibo; o motivo (pelo menos ${MENOR_MOTIVO_DO_REENVIO} caracteres) fica na trilha.`
            : "Sai agora. O recibo registra a entrega; sem recibo, a tentativa fica na lista, com o motivo."}
        </span>
      </div>
      {erro && (
        <p role="alert" className="text-vermelho">
          {erro}
        </p>
      )}
    </section>
  );
}

/** O menor motivo de uma anulação — o servidor confere (`MENOR_MOTIVO_DA_ANULACAO`). */
const MENOR_MOTIVO = 10;

function DatasDoContrato({
  concessao,
  datas,
  podeDefinir,
  recarregar,
}: {
  concessao: ConcessaoNoPoderConcedente;
  datas: DataDoContrato[];
} & AoMudar) {
  return (
    <Cartao titulo="Datas do contrato">
      {datas.map((data) =>
        podeDefinir ? (
          <DataEditavel
            key={data.chave}
            data={data}
            concessao={concessao}
            recarregar={recarregar}
          />
        ) : (
          <Linha key={data.chave} rotulo={data.rotulo}>
            {data.valor
              ? data.opcoes
                ? data.valor
                : dataDoDia(data.valor)
              : "não informada"}
          </Linha>
        ),
      )}
    </Cartao>
  );
}

function DataEditavel({
  data,
  concessao,
  recarregar,
}: {
  data: DataDoContrato;
  concessao: ConcessaoNoPoderConcedente;
  recarregar: () => Promise<void>;
}) {
  const [valor, definirValor] = useState(data.valor);
  const [gravando, definirGravando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => definirValor(data.valor), [data.valor]);

  async function gravar() {
    definirGravando(true);
    try {
      await api(`/admin/configuracoes/${data.chave}`, {
        method: "PUT",
        corpo: { valor, concessao_id: concessao.concessao_id },
      });
      definirErro(null);
      await recarregar();
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível gravar a data."));
    } finally {
      definirGravando(false);
    }
  }

  const rotulo = `${data.rotulo} de ${concessao.concessao}`;
  const estilo =
    "min-h-8 rounded-botao border border-linha bg-painel px-2 text-[16px] text-tinta md:text-[11.5px]";
  return (
    <div className="grid gap-1 text-[11.5px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-tinta-2">{data.rotulo}</span>
        {data.opcoes ? (
          <select
            aria-label={rotulo}
            value={valor}
            onChange={(e) => definirValor(e.target.value)}
            className={estilo}
          >
            {data.opcoes.map((opcao) => (
              <option key={opcao} value={opcao}>
                {opcao || "não informado"}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="date"
            aria-label={rotulo}
            value={valor}
            onChange={(e) => definirValor(e.target.value)}
            className={estilo}
          />
        )}
        <Botao
          papel="primaria"
          disabled={gravando || valor === data.valor}
          onClick={() => void gravar()}
        >
          Gravar
        </Botao>
      </div>
      <span className="text-[11px] text-tinta-3">{data.descricao}</span>
      {erro && (
        <p role="alert" className="text-vermelho">
          {erro}
        </p>
      )}
    </div>
  );
}
